import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SiteSelector } from 'src/features/components/granularSLA/SiteSelector';
import { useUser } from 'src/utils/UserContext';
import { LoadingState } from '../../components/granularSLA/LoadingState';
import { MainContent } from '../../components/granularSLA/MainContent';
import { useGSLAData } from '../../hooks/useGSLAData';
import { useGSLAColumns } from '../../hooks/useGSLAColumns';
import { DEFAULT_WHT } from 'src/types';
import { getValidWHTsForCountry } from 'src/types/WarehouseHandlingTier';
import { getFCMetadata } from 'src/utils/Queries';

import { exportToCSV } from '../../utils/exportHelpers';

import '../../styles/granularSLA/productionCapability.scss';


const BulkProductionCapabilityManager = () =>
{
    // Hooks and State
    const { user } = useUser();
    const tableReference = React.useRef<any>();
    
    // Check if user has edit permissions
    const hasEditPermission = user?.isAuthorizedForSLAOverrides === true || user?.isDevAdmin === true;
    
    // State for view-only mode - default to false for users with permissions
    const [viewOnlyMode, setViewOnlyMode] = useState<boolean>(false);

    //const siteIdList =
     //["ABQ1","ACY1","AGS1","AKC1","ATL2","AUS2","AUS3","BDL2","BDL3","BDL4","BFI4","BFL1","BHM1","BOI2","BOS3","BTR1","BWI2","CLE2","CLE3","CLT4","CMH1","CMH4","DAL3","DCA1","DEN3","DEN4","DET3","DET6","DFW7","DSM5","DTW1","ELP1","EWR4","EWR9","FAT1","FSD1","FTW6","FWA6","GEG1","GRR1","GYR1","HOU2","HOU6","IGQ1","JAN1","JAX2","JFK8","LAS7","LGA9","LGB3","LGB7","LIT1","LUK2","MCO1","MDW7","MEM4","MIA1","MKC6","MKE1","MKE2","MLI1","MQY1","MSP1","MTN1","OAK4","OKC1","OMA2","ORD5","ORF3","ORH3","OXR1","PAE2","PCW1","PDX9","PSP1","PVD2","RDU1","RIC4","ROC1","SAN3","SAT2","SAT3","SAV4","SBD6","SCK6","SHV1","SLC1","SMF1","STL8","SYR1","TLH2","TPA1","TPA4","TUL2","TUS2","TYS1","VGT1","YEG2","YHM1","YOW3","YXU1","YYC4","YYZ4"];
    const [siteIdList, setSiteIdList] = useState<string[]>([ 'DEN4', 'OAK4', 'ABQ1' ]);
    const [selectedHandlingTier, setSelectedHandlingTier] = useState<string>(DEFAULT_WHT);
    const [availableTiers, setAvailableTiers] = useState<string[] | undefined>(undefined);

    // Fetch FC metadata for all selected sites and compute union of valid WHTs
    useEffect(() => {
        if (siteIdList.length === 0) return;

        const fetchCountryWHTs = async () => {
            try {
                const metadataPromises = siteIdList.map(async (id) => {
                    try {
                        return await getFCMetadata(id);
                    } catch {
                        return null;
                    }
                });
                const results = await Promise.all(metadataPromises);

                const allWHTs = new Set<string>();
                results.forEach(metadata => {
                    if (metadata) {
                        getValidWHTsForCountry(metadata.countryCode).forEach(wht => allWHTs.add(wht));
                    }
                });

                if (allWHTs.size > 0) {
                    setAvailableTiers(Array.from(allWHTs));
                }
            } catch (error) {
                console.error('Error fetching FC metadata for WHT filtering:', error);
                // Fall back to default WHTs on error
            }
        };

        fetchCountryWHTs();
    }, [siteIdList]);

    const {
        gslaData,
        gslaRecommendations,
        isGSLADataLoading,
        isGSLARecommendationsLoading,
        productionCapabilitySet,
        mergedData
    } = useGSLAData(siteIdList, selectedHandlingTier);

    const productionCapabilityArray = useMemo(() => Array.from( productionCapabilitySet ).sort(), [productionCapabilitySet]);

    const handleExportCSV = useCallback(() => {
        try {
            exportToCSV(gslaData, gslaRecommendations, productionCapabilityArray, selectedHandlingTier);
        } catch (error) {
        }
    }, [gslaData, gslaRecommendations, productionCapabilityArray, selectedHandlingTier]);

    const { columns: fullDataTableColums } = useGSLAColumns(productionCapabilitySet);
    
    // Function to toggle view-only mode
    const toggleViewMode = () => {
        setViewOnlyMode(prev => !prev);
    };
    
    // Determine if user is authorized based on permissions and view mode
    const isAuthorized = hasEditPermission && !viewOnlyMode;


    // Show loading state while data is being fetched
    if (isGSLADataLoading || isGSLARecommendationsLoading) {
        return <LoadingState />;
    }
    
    // Show site selector if no sites are selected
    if (siteIdList.length === 0) {
        return <SiteSelector onSiteSelect={setSiteIdList} />;
    }

    return (
        <React.Fragment>
            {/* Only show toggle if user has edit permissions */}
            {hasEditPermission && (
                <div style={{ 
                    padding: '16px', 
                    backgroundColor: '#f5f5f5', 
                    borderBottom: '1px solid #ddd',
                    marginBottom: '16px' 
                }}>
                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                        <input 
                            type="checkbox" 
                            checked={viewOnlyMode} 
                            onChange={toggleViewMode}
                            style={{ marginRight: '8px' }}
                        />
                        <span style={{ fontWeight: 500 }}>View-Only Mode</span>
                    </label>
                </div>
            )}
            
            <MainContent
                onSiteSelect={setSiteIdList}
                onExport={handleExportCSV}
                isLoading={isGSLADataLoading || isGSLARecommendationsLoading}
                columns={fullDataTableColums}
                data={mergedData?? []}
                tableRef={tableReference}
                isAuthorized={isAuthorized}
                selectedHandlingTier={selectedHandlingTier}
                onHandlingTierChange={setSelectedHandlingTier}
                availableTiers={availableTiers}
            />
        </React.Fragment>
    );
};

export default BulkProductionCapabilityManager;
